import { useState, useEffect } from "react";
import { useUser } from "./useUser";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateProfile, resetPassword } from "service/settings";
import { toast } from "sonner";
import { getTokenFromLocalStore, saveTokenToLocalStore } from "utils";

interface IUpdateProfileFormData {
  phone?: string;
  email?: string;
  firstname?: string;
  surname?: string;
  other_names?: string;
  gender?: string;
  whatsapp_number?: string | null;
  occupation?: string;
  height?: number;
  weight?: number;
  dob?: string;
  profile_pic?: File | null;
}

interface IResetPassword {
  currentPassword?: string;
  newPassword?: string;
  confirmNewPassword?: string;
}

interface IRestPasswordResponse {
  message: string;
}

interface IUserDetails {
  email: string;
  inoId: string;
  phone: string;
  whatsappNumber: string | null;
  surname: string;
  firstname: string;
  otherNames: string;
  gender: string;
  occupation: string | null;
  height: string | null;
  weight: string | null;
  dob: string | null;
  age: string | null;
  status: string;
  profilePic: string | null;
}

export const useSettings = () => {
  const { user, setUser } = useUser();

  const [formData, setFormData] = useState<Partial<IResetPassword>>({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });

  const [updateProfileFormData, setUpdateProfileFormData] = useState<
    Partial<IUpdateProfileFormData>
  >({
    phone: user?.phone ?? undefined,
    email: user?.email ?? undefined,
    firstname: user?.firstname ?? undefined,
    surname: user?.surname ?? undefined,
    other_names: user?.otherNames ?? undefined,
    gender: user?.gender ?? undefined,
    whatsapp_number: user?.whatsappNumber ?? undefined,
    occupation: user?.occupation ?? undefined,
    height: user?.height ? parseFloat(user.height) : undefined,
    weight: user?.weight ? parseFloat(user.weight) : undefined,
    dob: user?.dob ?? undefined,
  });

  useEffect(() => {
    if (user) {
      setUpdateProfileFormData({
        phone: user.phone,
        email: user.email,
        firstname: user.firstname,
        surname: user.surname,
        other_names: user.otherNames,
        gender: user.gender,
        whatsapp_number:
          user.whatsappNumber === "None" ? null : user.whatsappNumber,
        occupation: user.occupation ?? undefined,
        height: user.height ? parseFloat(user.height) : undefined,
        weight: user.weight ? parseFloat(user.weight) : undefined,
        dob: user.dob ?? undefined,
      });
    }
  }, [user]);

  // Handle input changes for password reset
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  // Handle file input change for profile picture
  const handleProfilePicChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0] || null;
    setUpdateProfileFormData((prevData) => ({
      ...prevData,
      profile_pic: file,
    }));
  };

  // Handle input changes for profile update
  const handleUpdateProfileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = event.target;
    setUpdateProfileFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  // Handle password reset submission
  const handleReset = (e: React.FormEvent) => {
    e.preventDefault();
    resetPasswordMutation();
  };

  const { mutate: resetPasswordMutation, isPending: resetPasswordIsPending } =
    useMutation<IRestPasswordResponse>({
      mutationFn: async () => {
        return await resetPassword(
          formData.currentPassword || "",
          formData.newPassword || "",
          formData.confirmNewPassword || ""
        );
      },
      onSuccess: (data) => {
        toast.success(data.message);
        setFormData({
          currentPassword: "",
          newPassword: "",
          confirmNewPassword: "",
        });
      },
      onError: (error) => {
        toast.error("Failed to reset password");
      },
    });

  // Handle profile update submission
  const { mutate: updateProfileMutation, isPending: updateProfileIsPending } =
    useMutation({
      mutationFn: async () => {
        // Transform whatsapp_number before sending
        const transformedData = {
          ...updateProfileFormData,
          whatsapp_number:
            updateProfileFormData.whatsapp_number === "None"
              ? "None" // Keep as string for FormData, backend handles as null
              : updateProfileFormData.whatsapp_number,
        };
        return updateProfile(transformedData);
      },
      // ...
      onSuccess: (response) => {
        const updatedUser = {
          ...user,
          email: response.data.email || user?.email,
          phone: response.data.phone || user?.phone,
          whatsappNumber:
            response.data.whatsapp_number === "null"
              ? null
              : response.data.whatsapp_number || user?.whatsappNumber,
          surname: response.data.surname || user?.surname,
          firstname: response.data.firstname || user?.firstname,
          otherNames: response.data.other_names || user?.otherNames,
          gender: response.data.gender || user?.gender,
          occupation: response.data.occupation || user?.occupation,
          height: response.data.height || user?.height,
          weight: response.data.weight || user?.weight,
          dob: response.data.dob || user?.dob,
          profilePic: response.data.profile_pic || user?.profilePic,
        };
        setUser(updatedUser);
        const userDetails =
          getTokenFromLocalStore<IUserDetails>("user_details");
        saveTokenToLocalStore("user_details", {
          ...userDetails,
          ...updateProfileFormData,
          profilePic: response.data.profile_pic,
        });
        toast.success("Profile updated successfully!");
      },
      onError: (error: any) => {
        console.error("Update Error:", error);
        toast.error(error.message || "Failed to update profile");
      },
    });

  const handleUpdateProfileSubmit = () => {
    updateProfileMutation();
  };

  return {
    updateProfileFormData,
    formData,
    resetPasswordIsPending,
    updateProfileIsPending,
    handleInputChange,
    handleUpdateProfileInputChange,
    handleProfilePicChange,
    handleUpdateProfileSubmit,
    handleReset,
    user,
  };
};
